import { useEffect, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import moment from "moment";
import TimeWindowController from "./TimeWindowController";
import SuggestionInput from "@src/orders/create/components/SuggestionInput";
import {
  EndCustomer,
  Shipment,
  ShipmentStopType,
} from "@api/graphql/generated/generated-types";
import { Label, Select, Textarea } from "@src/common/components";
import StandardTimeRow from "./StandardTimeRow";
import TimeRowWithTimeWindow from "./TimeRowWithTimeWindow";
import useDriverQuestionListOptions from "@src/common/hooks/useDriverQuestionListOptions";

type Props = {
  stopIndex: number;
  selectedCustomer: EndCustomer | undefined;
  stopPathName: string;
  isRecurring: boolean;
  selectedDuration: string | undefined;
  onStopDateChange: (() => void) | undefined;
  selectedShipment: Shipment | undefined;
  showPickupCloseFields: boolean;
  showDropoffReadyFields: boolean;
  isSuiteRequired?: boolean;
  isPhoneNumberRequired?: boolean;
  isCompanyNameRequired?: boolean;
};

function StopInputs({
  stopIndex = 0,
  selectedCustomer,
  stopPathName = "stops",
  isRecurring = false,
  selectedDuration,
  onStopDateChange,
  selectedShipment,
  showPickupCloseFields = false,
  showDropoffReadyFields = false,
  isSuiteRequired = false,
  isPhoneNumberRequired = false,
  isCompanyNameRequired = false,
}: Props) {
  const { control, setValue, clearErrors, getValues, watch } = useFormContext();
  const initialRender = useRef(true);

  const { questionListOptions, questionListsLoading } =
    useDriverQuestionListOptions();

  const watchCloseTime = watch(
    `${stopPathName}[${stopIndex}].timeWindow.${
      isRecurring ? "closeSecondsSinceMidnightUtc" : "close"
    }`
  );
  const watchOpenTime = watch(
    `${stopPathName}[${stopIndex}].timeWindow.${
      isRecurring ? "openSecondsSinceMidnightUtc" : "open"
    }`
  );

  useEffect(() => {
    if (!selectedDuration) {
      return;
    }
    if (initialRender.current) {
      initialRender.current = false;
    } else {
      if (selectedDuration === "noStopDates") {
        setValue(`${stopPathName}[${stopIndex}].stopDate`, null);
        setValue(`${stopPathName}[${stopIndex}].deliverByDate`, null);
        // Only clear pickupByDate if pickup close fields are enabled
        if (showPickupCloseFields) {
          setValue(`${stopPathName}[${stopIndex}].pickupByDate`, null);
        }
      }
    }
  }, [selectedDuration]);

  const formatTime = (time: number) => {
    if (!time) {
      return;
    }
    if (isRecurring) {
      const exactMoment = moment.unix(time);
      return moment(exactMoment).format("HH:mm");
    }
    let formattedTime = moment(Number(time * 1000)).format("HH:mm");
    return formattedTime;
  };
  const stopType: ShipmentStopType = getValues(
    `${stopPathName}[${stopIndex}].type`
  );

  // Determine which time row component to render
  // Use extended time window row for pickup when showPickupCloseFields, or dropoff when showDropoffReadyFields
  const shouldUseTimeWindowRow =
    (showPickupCloseFields && stopType === ShipmentStopType.PickUp) ||
    (showDropoffReadyFields && stopType === ShipmentStopType.DropOff);

  const timeRowProps = {
    stopIndex,
    stopPathName,
    stopType,
    isRecurring,
    selectedDuration,
    onStopDateChange,
    selectedShipment,
    control,
    watch,
    getValues,
    formatTime,
    watchOpenTime,
    watchCloseTime,
    isSuiteRequired,
  };

  return (
    <>
      {shouldUseTimeWindowRow ? (
        <TimeRowWithTimeWindow {...timeRowProps} />
      ) : (
        <StandardTimeRow {...timeRowProps} />
      )}

      <div className={"col-span-2"}>
        <SuggestionInput
          control={control}
          type={"contactName"}
          secondaryType={"companyName"}
          selectedCustomer={selectedCustomer}
          setValue={setValue}
          getValues={getValues}
          clearErrors={clearErrors}
          shipmentStopsFormName={`${stopPathName}[${stopIndex}]`}
          isStopFullUpdate={true}
          usePositive={false}
        />
      </div>

      <div className={"col-span-1"}>
        <SuggestionInput
          control={control}
          type={"companyName"}
          secondaryType={"contactName"}
          selectedCustomer={selectedCustomer}
          setValue={setValue}
          getValues={getValues}
          clearErrors={clearErrors}
          shipmentStopsFormName={`${stopPathName}[${stopIndex}]`}
          isStopFullUpdate={true}
          usePositive={false}
          required={isCompanyNameRequired}
        />
      </div>

      <div className={"col-span-1"}>
        <SuggestionInput
          control={control}
          type={"phone"}
          selectedCustomer={selectedCustomer}
          setValue={setValue}
          getValues={getValues}
          clearErrors={clearErrors}
          shipmentStopsFormName={`${stopPathName}[${stopIndex}]`}
          isStopFullUpdate={true}
          usePositive={false}
          required={isPhoneNumberRequired}
        />
      </div>

      <div className={"col-span-1"}>
        <Label className="block text-xs font-medium text-gray-700 truncate">
          Driver Subtask List{" "}
          <span className="text-slate-400 text-xs">(Optional)</span>
        </Label>
        <Controller
          name={`${stopPathName}[${stopIndex}].additionalDriverQuestionListId`}
          control={control}
          render={({ field }) => (
            <Select
              size="mini"
              options={questionListOptions}
              isLoading={questionListsLoading}
              value={
                field.value
                  ? questionListOptions.filter((opt) => opt.id === field.value)
                  : []
              }
              searchable={true}
              clearable={true}
              placeholder="Select..."
              onChange={(params) => {
                if (!params.option || params.option.id === "") {
                  field.onChange(null);
                } else {
                  field.onChange(params.option.id);
                }
              }}
            />
          )}
        />
      </div>

      <div className="col-span-5">
        <Label className="block text-xs font-medium text-gray-700 truncate">
          Instruction
        </Label>
        <Controller
          name={`${stopPathName}[${stopIndex}].instruction`}
          control={control}
          render={({ field }) => (
            <Textarea
              size="mini"
              placeholder=""
              onChange={field.onChange}
              value={field.value}
            />
          )}
        />
      </div>

      {stopType === ShipmentStopType.Return && (
        <TimeWindowController
          stopIndex={stopIndex}
          label="Deliver By"
          name={`${stopPathName}[${stopIndex}].timeWindow.${
            isRecurring ? "closeSecondsSinceMidnightUtc" : "close"
          }`}
          control={control}
          formatTime={formatTime}
          watchValue={watch(
            `${stopPathName}[${stopIndex}].timeWindow.${
              isRecurring ? "closeSecondsSinceMidnightUtc" : "close"
            }`
          )}
        />
      )}
    </>
  );
}

export default StopInputs;
